import React, { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { Plus, Trash2, GripVertical, Save, ChevronDown, ChevronUp, GitBranch, RotateCcw, ShieldAlert } from "lucide-react";
import { toast } from "../../components/ui/sonner";

// Question types allowed on the H&S questionnaire. Photo/barcode/measurement
// are intentionally excluded — H&S is a plain-text safety declaration.
const TYPES = [
  { v: "short_text", l: "Short text" },
  { v: "long_text", l: "Long text" },
  { v: "dropdown", l: "Dropdown" },
  { v: "multi_select", l: "Multi-select" },
  { v: "yes_no", l: "Yes / No" },
  { v: "signature", l: "Signature pad" },
];

function branchingValuesFor(q) {
  if (!q) return null;
  if (q.type === "yes_no") return ["Yes", "No"];
  if (q.type === "dropdown" || q.type === "multi_select") return (q.options || []).filter(Boolean);
  return null;
}

export function HealthSafetyTab() {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/hs-questionnaire").then((r) => setDoc(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setQuestion = (i, patch) => {
    const next = [...(doc.questions || [])];
    next[i] = { ...next[i], ...patch };
    setDoc({ ...doc, questions: next });
  };
  const addQuestion = () => {
    const newId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `q-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    setDoc({
      ...doc,
      questions: [
        ...(doc.questions || []),
        { id: newId, label: "", type: "short_text", required: false, options: [], branching_rules: [] },
      ],
    });
  };
  const removeQuestion = (i) => setDoc({
    ...doc,
    questions: (doc.questions || []).filter((_, idx) => idx !== i),
  });
  const move = (i, dir) => {
    const j = i + dir;
    const qs = doc.questions || [];
    if (j < 0 || j >= qs.length) return;
    const next = [...qs];
    [next[i], next[j]] = [next[j], next[i]];
    setDoc({ ...doc, questions: next });
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        questions: (doc.questions || []).map((q) => ({
          ...q,
          label: (q.label || "").trim(),
          options: (q.options || []).map((o) => (o || "").trim()).filter(Boolean),
        })).filter((q) => q.label),
      };
      const { data } = await api.put("/hs-questionnaire", body);
      setDoc(data);
      toast.success("Health & Safety questionnaire saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Reset the H&S questionnaire to the built-in default set? Any custom questions will be lost.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/hs-questionnaire/reset");
      setDoc(data);
      toast.success("H&S questionnaire reset to default.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!doc) return <div className="text-slate-400 text-sm">Loading…</div>;
  const qs = doc.questions || [];

  return (
    <div className="space-y-5" data-testid="hs-tab">
      <div className="qb-card p-5 flex items-start gap-3 border-l-4 border-brand-orange">
        <ShieldAlert className="text-brand-orange mt-0.5 shrink-0" size={22} />
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-black tracking-tight">Health &amp; Safety Questionnaire</h2>
          <p className="text-sm text-slate-600 mt-1">
            This single questionnaire is appended to <b>every</b> site survey after the product questions
            are completed. Surveyors cannot mark a survey as complete without answering it.
            Changes here take effect on any new survey started from now on.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {qs.length} question{qs.length === 1 ? "" : "s"} · last updated{" "}
            {(doc.updated_at || "").slice(0, 19).replace("T", " ")}
            {doc.updated_by ? ` by ${doc.updated_by}` : ""}
          </p>
        </div>
        <button
          onClick={resetToDefault}
          disabled={busy}
          data-testid="hs-reset-default"
          className="h-9 px-3 border border-slate-300 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 hover:bg-slate-50"
          title="Rebuild from the built-in flowchart defaults"
        >
          <RotateCcw size={12} /> Reset to default
        </button>
      </div>

      <div className="qb-card p-5 space-y-4" data-testid="hs-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-700">Questions</h3>
          <button onClick={addQuestion} data-testid="hs-add-question"
            className="text-xs uppercase tracking-wider font-semibold text-brand-blue hover:underline inline-flex items-center gap-1">
            <Plus size={12} /> Add question
          </button>
        </div>

        <div className="space-y-2">
          {qs.length === 0 && <div className="text-slate-400 text-sm">No questions yet — click Add question above.</div>}
          {qs.map((q, i) => (
            <div key={q.id || i} className="border border-slate-200 rounded-md p-3 grid grid-cols-12 gap-2 items-start bg-slate-50/50" data-testid={`hs-question-row-${i}`}>
              <div className="col-span-1 flex flex-col items-center gap-1 pt-1.5 text-slate-400">
                <GripVertical size={12} />
                <button onClick={() => move(i, -1)} className="hover:text-slate-700" title="Move up"><ChevronUp size={12} /></button>
                <button onClick={() => move(i, 1)} className="hover:text-slate-700" title="Move down"><ChevronDown size={12} /></button>
              </div>
              <div className="col-span-5">
                <input className="qb-input" placeholder="Question label" value={q.label} onChange={(e) => setQuestion(i, { label: e.target.value })} data-testid={`hs-question-label-${i}`} />
              </div>
              <div className="col-span-3">
                <select className="qb-input" value={q.type} onChange={(e) => setQuestion(i, { type: e.target.value })} data-testid={`hs-question-type-${i}`}>
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div className="col-span-2 inline-flex items-center gap-2 pt-2">
                <label className="text-xs inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={!!q.required} onChange={(e) => setQuestion(i, { required: e.target.checked })} />
                  Required
                </label>
              </div>
              <div className="col-span-1 text-right">
                <button onClick={() => removeQuestion(i)} className="text-red-500 hover:text-red-700" title="Remove"><Trash2 size={14} /></button>
              </div>
              {(q.type === "dropdown" || q.type === "multi_select") && (
                <div className="col-span-12 pl-7">
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Options (one per line)</span>
                  <textarea className="qb-input min-h-[64px]"
                    value={(q.options || []).join("\n")}
                    onChange={(e) => setQuestion(i, { options: e.target.value.split("\n") })}
                    data-testid={`hs-question-options-${i}`}
                    placeholder={"Option 1\nOption 2"}
                  />
                </div>
              )}
              <div className="col-span-12 pl-7">
                <input className="qb-input" placeholder="Help text / hint (optional)"
                  value={q.help_text || ""}
                  onChange={(e) => setQuestion(i, { help_text: e.target.value })}
                />
              </div>
              <HSBranchingEditor
                i={i}
                q={q}
                allQuestions={qs}
                onChange={(rules) => setQuestion(i, { branching_rules: rules })}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
          <button onClick={save} disabled={busy} data-testid="hs-save"
            className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <Save size={12} /> {busy ? "Saving…" : "Save questionnaire"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HSBranchingEditor({ i, q, allQuestions, onChange }) {
  const supported = branchingValuesFor(q);
  const rules = q.branching_rules || [];
  const [open, setOpen] = useState(rules.length > 0);
  if (!supported) return null;

  const laterQuestions = allQuestions
    .slice(i + 1)
    .map((qq, offset) => ({ q: qq, indexLabel: i + 2 + offset }));

  const setRuleTarget = (value, targetId) => {
    const next = rules.filter((r) => r.value !== value);
    if (targetId) next.push({ value, target_id: targetId });
    onChange(next);
  };
  const ruleFor = (value) => rules.find((r) => r.value === value)?.target_id || "";
  const ruleCount = rules.filter((r) => r.target_id).length;

  return (
    <div className="col-span-12 pl-7">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-brand-orange hover:underline mt-2"
      >
        <GitBranch size={11} />
        {ruleCount > 0 ? (
          <>
            {open ? "Hide" : "View"} conditional branching
            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-brand-orange text-white text-[10px] tracking-normal">{ruleCount}</span>
          </>
        ) : (
          <>{open ? "Hide" : "Add"} conditional branching</>
        )}
      </button>
      {open && (
        <div className="mt-2 border border-orange-200 bg-orange-50/40 rounded p-3 space-y-2">
          <p className="text-[11px] text-slate-600">
            For each possible answer, optionally pick the next question to jump to. Questions in between are auto-hidden.
          </p>
          {supported.map((val) => (
            <div key={val} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-3 text-xs">
                <span className="text-slate-500">If answer =</span>{" "}
                <span className="font-semibold text-slate-900">{val}</span>
              </div>
              <div className="col-span-1 text-center text-slate-400">→</div>
              <div className="col-span-8">
                <select
                  className="qb-input"
                  value={ruleFor(val)}
                  onChange={(e) => setRuleTarget(val, e.target.value)}
                >
                  <option value="">— follow normal order —</option>
                  {laterQuestions.map(({ q: qq, indexLabel }) => (
                    <option key={qq.id} value={qq.id}>
                      Q{indexLabel}: {(qq.label || "").slice(0, 60) || "(no label yet)"}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
